import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { photo, photoSrcSet } from '../assets/photos';
import Seo from '../components/Seo';

const Reveal = ({
  children, delay = 0, className = '', y = 24,
}: {
  children: React.ReactNode; delay?: number; className?: string; y?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Locations = () => {
  const categories = [
    {
      region: 'Pretoria Area',
      description: 'The heart of Pretoria and surrounding areas',
      venues: ['Rosemary Hill', 'The Greenhouse Cafe', 'Lance on Timber'],
      image: 'nastasha-lohan/5.jpg',
    },
    {
      region: 'Eastern Free State',
      description: 'Dramatic landscapes and rustic highland charm',
      venues: ['Rosendal Eastern Free State', 'Monate Game Lodge', 'Rolling Waters Country Estate'],
      image: 'amber-erik/5.jpg',
    },
    {
      region: 'Guest Farms',
      description: 'Intimate and characterful farm settings',
      venues: ['Florence Guest Farm', 'De Uijlenes', 'De Harte', 'Welgelegen'],
      image: 'nkateko-fika/5.jpg',
    },
    {
      region: 'Game Farms & Estates',
      description: 'Wild and luxurious — an unforgettable backdrop',
      venues: ['Tshikwalo', 'The Nut Farm', 'Smitsfield', 'The Silver Sixpence', 'Lunikhy Game Farm', 'Kleinkaap'],
      image: 'jordane-riegal/5.jpg',
    },
  ];

  const allVenues = [
    { name: 'Rosemary Hill',                  region: 'Pretoria' },
    { name: 'The Greenhouse Cafe',             region: 'Pretoria' },
    { name: 'Lance on Timber',                 region: 'Pretoria' },
    { name: 'Rosendal Eastern Free State',     region: 'Free State' },
    { name: 'Monate Game Lodge',               region: 'Free State' },
    { name: 'Rolling Waters Country Estate',   region: 'Free State' },
    { name: 'Florence Guest Farm',             region: 'Guest Farm' },
    { name: 'De Uijlenes',                     region: 'Guest Farm' },
    { name: 'De Harte',                        region: 'Guest Farm' },
    { name: 'Welgelegen',                      region: 'Guest Farm' },
    { name: 'Tshikwalo',                       region: 'Game Farm' },
    { name: 'The Nut Farm',                    region: 'Game Farm' },
    { name: 'Smitsfield',                      region: 'Game Farm' },
    { name: 'The Silver Sixpence',             region: 'Game Farm' },
    { name: 'Lunikhy Game Farm',               region: 'Game Farm' },
    { name: 'Kleinkaap',                       region: 'Game Farm' },
  ];

  return (
    <div className="min-h-screen bg-ivory overflow-x-hidden">
      <Seo route="/locations" />

      {/* ── Page Header ─────────────────────────────────────────── */}
      <div className="page-header bg-ivory px-8 md:px-14 lg:px-20">
        <div className="max-w-screen-xl mx-auto">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="label-sm text-mist mb-5"
          >
            Where We Shoot
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="heading-display text-ink text-[clamp(3rem,7vw,7rem)] mb-6"
          >
            Locations
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="body-light max-w-xl"
          >
            From intimate garden weddings in Pretoria to grand celebrations across South Africa and beyond — we'll go wherever your story takes you.
          </motion.p>
        </div>
      </div>

      {/* ── Featured venue — Rosemary Hill ──────────────────────── */}
      <section className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20 pb-16 md:pb-24">
        <Reveal className="grid grid-cols-1 lg:grid-cols-12 overflow-hidden">
          <div className="lg:col-span-8 overflow-hidden">
            <div className="overflow-hidden h-[50vw] max-h-[600px] min-h-[300px]">
              <img loading="lazy" decoding="async"
                src={photo('mel-brandon/5.jpg')}
                alt="Wedding at Rosemary Hill, Pretoria"
                className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-[1400ms] ease-out"
              />
            </div>
          </div>

          <div className="lg:col-span-4 bg-ink text-white p-8 md:p-12 flex flex-col justify-between">
            <div>
              <p className="label-sm text-champagne mb-6">Featured Venue</p>
              <h2 className="heading-display text-white text-3xl md:text-4xl mb-5">Rosemary Hill</h2>
              <p className="body-light text-white/55 mb-8 text-sm md:text-[0.9375rem]">
                One of our most beloved venues — stunning gardens and elegant architecture create the perfect backdrop for your special day.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Garden Weddings', 'Elegant Architecture', 'Natural Beauty'].map(tag => (
                  <span key={tag} className="label-sm text-white/60 border border-white/15 px-3 py-1.5">{tag}</span>
                ))}
              </div>
            </div>
            <div className="mt-10">
              <Link to="/contact" className="link-underline text-white/65">
                Inquire about this venue <span className="ml-1">→</span>
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ── Region categories ────────────────────────────────────── */}
      <section className="bg-bone py-20 md:py-32">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
          <Reveal>
            <p className="label-sm text-mist mb-5">By Region</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="heading-display text-ink text-[clamp(2.4rem,5vw,5rem)] mb-16">
              Where we've captured
              <br />
              <span className="heading-italic">love stories</span>
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {categories.map((cat, i) => (
              <Reveal key={cat.region} delay={i * 0.08}>
                <div className="relative overflow-hidden group">
                  <div className="overflow-hidden h-[40vw] max-h-[440px] min-h-[240px]">
                    <img loading="lazy" decoding="async"
                      src={photo(cat.image)}
                      srcSet={photoSrcSet(cat.image)}
                      sizes="(min-width: 768px) 50vw, 100vw"
                      alt={`Wedding photography — ${cat.region}`}
                      className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1400ms] ease-out"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-7 md:p-9">
                    <p className="label-sm text-white/75 mb-2">{cat.venues.length} venues</p>
                    <h3 className="heading-display text-white text-2xl md:text-3xl mb-2">{cat.region}</h3>
                    <p className="label-sm text-white/80 mb-5">{cat.description}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {cat.venues.slice(0, 3).map(v => (
                        <span key={v} className="label-sm text-white/85 border border-white/30 px-2.5 py-1">{v}</span>
                      ))}
                      {cat.venues.length > 3 && (
                        <span className="label-sm text-white/60 px-2.5 py-1">+{cat.venues.length - 3} more</span>
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Complete venue list ──────────────────────────────────── */}
      <section className="bg-ivory py-20 md:py-32">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
          <Reveal>
            <p className="label-sm text-mist mb-5">Complete List</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="heading-display text-ink text-[clamp(2rem,4vw,4rem)] mb-14">
              35+ venues &amp; counting
            </h2>
          </Reveal>

          <div className="divide-y divide-silver/40">
            {allVenues.map((v, i) => (
              <Reveal key={v.name} delay={i * 0.03} y={12}>
                <div className="flex justify-between items-center py-4 group">
                  <h3 className="heading-display text-ink text-lg md:text-xl group-hover:text-champagne transition-colors duration-400">
                    {v.name}
                  </h3>
                  <span className="label-sm text-mist">{v.region}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────────────── */}
      <section className="bg-charcoal py-16 md:py-20">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { n: '35+',  l: 'Venues' },
              { n: '7+',   l: 'Countries' },
              { n: '500+', l: 'Couples' },
              { n: '200+', l: 'km included travel' },
            ].map(({ n, l }, i) => (
              <Reveal key={l} delay={i * 0.07}>
                <div className="heading-display text-white text-[clamp(2.5rem,4.5vw,4rem)] mb-2">{n}</div>
                <p className="label-sm text-white/60">{l}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────── */}
      <section className="bg-ivory py-20 md:py-28">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
          <Reveal className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <p className="label-sm text-mist mb-5">Don't see your venue?</p>
              <h2 className="heading-display text-ink text-[clamp(2rem,4.5vw,4.5rem)]">
                We travel throughout South Africa
                <br />
                <span className="heading-italic">and internationally.</span>
              </h2>
            </div>
            <div className="md:col-span-5 flex md:justify-end">
              <Link to="/contact" className="link-underline text-ink">
                Inquire about your venue <span className="ml-1">→</span>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Locations;
